import { millisecondsPer, object } from '@art-suite/art-core-ts';
import { useCachedQuery, useQueryCache } from '@kaiizen/react-persistent-cache';
import { useMemo } from 'react';
import { useAuth } from '../useAuth';
import { useKaiProApiClient } from '../useKaiProApiClient';

import {
  ApiFeatureFlagRecord,
  ApiFeatureFlags,
  ApiFeatureFlagScope,
  DEFAULT_NAMED_FEATURE_FLAGS,
  FeatureFlagName,
  getAllFeatureFlagRecordsQueryKey,
  getFeatureFlagsQueryKey,
  NAMED_FEATURE_FLAGS,
  normalizeFeatureFlagName,
} from '@kaiizen/kai-pro-api';

/**
 * Maps UI context (org/user pickers use "" for “all”) to API payload: omit absent ids so the
 * server stores SQL NULL for global scope. Matches SuperAdminFeatureFlagsManager scope rules.
 */
const resolveFeatureFlagScopeForSetRequest = (
  orgId: string | undefined,
  userId: string | undefined,
): { scope: ApiFeatureFlagScope; orgId?: string; userId?: string } => {
  const resolvedOrgId = orgId?.trim() || undefined;
  const resolvedUserId = userId?.trim() || undefined;
  if (resolvedOrgId && resolvedUserId) {
    return { scope: 'user', userId: resolvedUserId };
  }
  if (resolvedOrgId) {
    return { scope: 'org', orgId: resolvedOrgId };
  }
  if (resolvedUserId) {
    return { scope: 'user', userId: resolvedUserId };
  }
  return { scope: 'global' };
};

const getNamedFeatureFlagsFromApiFeatureFlags = (
  apiFeatureFlags: ApiFeatureFlags,
): Record<FeatureFlagName, boolean> => {
  const normalizedFlags = Object.entries(apiFeatureFlags).reduce<
    Record<string, boolean>
  >(
    (flags, [flagName, enabled]) => ({
      ...flags,
      [normalizeFeatureFlagName(flagName)]: enabled,
    }),
    {},
  );
  return object(
    NAMED_FEATURE_FLAGS,
    (v, k) => normalizedFlags[k] ?? false,
  ) as unknown as Record<FeatureFlagName, boolean>;
};

export const useFeatureFlags = () => {
  const kaiProApiClient = useKaiProApiClient();
  const { userId, orgId } = useAuth();

  const query = useCachedQuery(
    getFeatureFlagsQueryKey(userId, orgId),
    async () =>
      getNamedFeatureFlagsFromApiFeatureFlags(
        await kaiProApiClient.getCurrentFeatureFlags(),
      ),
    {
      enabled: !!kaiProApiClient.token,
      staleAgeMs: 0,
      maxAgeMs: millisecondsPer.hour,
    },
  );

  const featureFlags = query.data ?? DEFAULT_NAMED_FEATURE_FLAGS;
  const featureFlagsReady = !kaiProApiClient.token || query.data !== undefined;

  return {
    ...query,
    featureFlags,
    featureFlagsReady,
  };
};

const useFeatureFlagRecordMutators = (refetchFlagRecords: () => void) => {
  const kaiProApiClient = useKaiProApiClient();
  const queryCache = useQueryCache();

  return useMemo(() => {
    const refetchFeatureFlags = async () => {
      await refetchFlagRecords();
      await queryCache.refetchAll({
        prefix: 'feature-flags/',
        immediate: true,
        reason: 'feature flag record changed',
      });
    };

    const saveFlagForCurrentContext = async (
      flagName: string,
      enabled: boolean,
      orgId?: string,
      userId?: string,
    ) => {
      await kaiProApiClient.setFeatureFlag({
        flagName,
        enabled,
        ...resolveFeatureFlagScopeForSetRequest(orgId, userId),
      });
      await refetchFeatureFlags();
    };

    return {
      saveFlagForCurrentContext,

      deleteFeatureFlagRecord: async (featureFlagId: string) => {
        await kaiProApiClient.deleteFeatureFlag(featureFlagId);
        await refetchFeatureFlags();
      },

      toggleFeatureFlagRecordEnabled: async (flag: ApiFeatureFlagRecord) =>
        saveFlagForCurrentContext(
          flag.flagName,
          !flag.enabled,
          flag.orgId,
          flag.userId,
        ),
    };
  }, [kaiProApiClient, queryCache, refetchFlagRecords]);
};

export const useAllFeatureFlagRecords = () => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getAllFeatureFlagRecordsQueryKey(),
    () => kaiProApiClient.getAllFeatureFlagRecords(),
    { enabled: !!kaiProApiClient.token },
  );

  return {
    ...query,
    allFeatureFlagRecords: query.data ?? [],
    featureFlagRecordMutators: useFeatureFlagRecordMutators(query.refetch),
  };
};
